import React from 'react';
import { cn } from '../utils/cn';

export type TypingIndicatorProps = {
  /** Optional text shown next to the dots (e.g. "Thinking..."). */
  label?: string;
  className?: string;
};

/**
 * Animated "typing" indicator for streaming/processing assistant responses.
 * Wraps three dots in a subtle rounded bubble so it reads as an incoming
 * message rather than free-floating dots.
 */
export const TypingIndicator: React.FC<TypingIndicatorProps> = ({ label, className }) => {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 rounded-full',
        'bg-bg-neutral-7/60 px-3 py-2',
        'border border-border/40',
        className
      )}
      role="status"
      aria-live="polite"
      aria-label={label ?? 'Assistant is typing'}
    >
      <div className="flex items-end gap-1">
        <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full typing-dot" />
        <span
          className="w-1.5 h-1.5 bg-muted-foreground rounded-full typing-dot"
          style={{ animationDelay: '0.15s' }}
        />
        <span
          className="w-1.5 h-1.5 bg-muted-foreground rounded-full typing-dot"
          style={{ animationDelay: '0.3s' }}
        />
      </div>
      {label && (
        <span className="text-xs text-text-secondary animate-pulse select-none">{label}</span>
      )}
    </div>
  );
};
